import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CaretDownIcon, CheckIcon, MapPinIcon } from '@phosphor-icons/react';

import classNames from '../../../../utils/classnames';
import { WeeklyTripStatusSchema } from '../../endpoints/weekly/PlanningBoardWeeklyEndpoint.types';
import type { WeeklyTripStatus } from '../../models';

interface StatusStyle {
  label: string;
  /** Pressing this key in the open menu picks the status. */
  shortcut: string;
  dot: string;
  halo: string;
  pill: string;
}

const STATUS_STYLES: Record<WeeklyTripStatus, StatusStyle> = {
  Ok: {
    label: 'Ok',
    shortcut: '1',
    dot: 'bg-positive',
    halo: 'bg-positive-subtle',
    pill: 'border-transparent bg-positive-subtle text-positive-text',
  },
  InTransit: {
    label: 'In transit',
    shortcut: '2',
    dot: 'bg-transit',
    halo: 'bg-transit-subtle',
    pill: 'border-transparent bg-transit-subtle text-transit-text',
  },
  Risk: {
    label: 'Risico',
    shortcut: '3',
    dot: 'bg-brand',
    halo: 'bg-warning-subtle',
    pill: 'border-brand-border bg-warning-subtle text-warning-text',
  },
  MajorIssue: {
    label: 'Groot probleem',
    shortcut: '4',
    dot: 'bg-negative',
    halo: 'bg-negative-subtle',
    pill: 'border-transparent bg-negative-subtle text-negative-text',
  },
};

const STATUSES = WeeklyTripStatusSchema.options;
const NO_STATUS_SHORTCUT = '0';

interface StatusBadgeProps {
  status: WeeklyTripStatus | null;
  children: React.ReactElement;
}

/** A loading/unloading icon inside a dot in the colour of its status; grey without one. */
export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, children }) => (
  <span
    title={status ? STATUS_STYLES[status].label : undefined}
    className={classNames(
      'flex size-4 shrink-0 items-center justify-center rounded-full [&>svg]:size-2.5',
      status ? classNames(STATUS_STYLES[status].dot, 'text-fg-inverse') : 'bg-neutral-subtle text-fg-muted',
    )}
  >
    {children}
  </span>
);

interface TripStatusSelectProps {
  value: WeeklyTripStatus | null;
  onChange: (status: WeeklyTripStatus | null) => void;
  /** Menu title, e.g. "Status lossen". */
  title: string;
  /** Shown next to the title, e.g. "Rit 2". */
  context: string;
}

/** Status pill of a loading or unloading; opens a menu with keyboard shortcuts 1–4 and 0. */
export const TripStatusSelect: React.FC<TripStatusSelectProps> = ({ value, onChange, title, context }) => {
  const [open, setOpen] = useState(false);
  const style = value ? STATUS_STYLES[value] : null;

  const pick = (status: WeeklyTripStatus | null) => {
    onChange(status);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Leave browser shortcuts like Cmd+1 alone.
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === NO_STATUS_SHORTCUT) {
      e.preventDefault();
      pick(null);
      return;
    }
    const status = STATUSES.find((s) => STATUS_STYLES[s].shortcut === e.key);
    if (status) {
      e.preventDefault();
      pick(status);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`${title}: ${style?.label ?? 'geen status'}`}
          className={classNames(
            'inline-flex h-[22px] shrink-0 items-center gap-1.5 rounded-full border pl-2 pr-[7px] text-2xs font-medium transition-opacity hover:opacity-80',
            style?.pill ?? 'border-border text-fg-muted',
          )}
        >
          <StatusDot status={value} className="size-[7px]" />
          {style?.label ?? 'Status'}
          <CaretDownIcon className="h-3 w-3" weight="bold" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          onKeyDown={handleKeyDown}
          className="z-dropdown flex w-[244px] flex-col gap-px rounded-[10px] border border-border bg-surface p-1.5 shadow-lg"
        >
          <div className="flex items-center justify-between px-2.5 pb-2 pt-1.5 text-2xs text-fg-muted">
            <span className="font-semibold">{title}</span>
            <span>{context}</span>
          </div>
          {STATUSES.map((status) => (
            <StatusOption
              key={status}
              status={status}
              label={STATUS_STYLES[status].label}
              shortcut={STATUS_STYLES[status].shortcut}
              isSelected={value === status}
              onSelect={() => pick(status)}
            />
          ))}
          <div className="py-1">
            <div className="h-px bg-border" />
          </div>
          <StatusOption
            status={null}
            label="Geen status"
            shortcut={NO_STATUS_SHORTCUT}
            isSelected={value == null}
            onSelect={() => pick(null)}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

interface StatusOptionProps {
  status: WeeklyTripStatus | null;
  label: string;
  shortcut: string;
  isSelected: boolean;
  onSelect: () => void;
}

const StatusOption: React.FC<StatusOptionProps> = ({ status, label, shortcut, isSelected, onSelect }) => (
  <button
    type="button"
    aria-pressed={isSelected}
    onClick={onSelect}
    className={classNames(
      'flex h-[34px] w-full items-center gap-2.5 rounded-md px-2 text-left text-sm outline-hidden transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover',
      isSelected && 'bg-surface-hover',
      status ? 'font-medium text-fg' : 'text-fg-secondary',
    )}
  >
    <span
      className={classNames(
        'flex size-[18px] shrink-0 items-center justify-center rounded-full',
        status && STATUS_STYLES[status].halo,
      )}
    >
      <StatusDot status={status} className="size-2" />
    </span>
    <span className="flex-1">{label}</span>
    {isSelected ? (
      <CheckIcon className="h-[15px] w-[15px] shrink-0 text-fg" weight="bold" />
    ) : (
      <kbd className="flex size-[18px] shrink-0 items-center justify-center rounded border border-border font-sans text-[10.5px] font-medium text-fg-muted">
        {shortcut}
      </kbd>
    )}
  </button>
);

const StatusDot: React.FC<{ status: WeeklyTripStatus | null; className: string }> = ({ status, className }) => (
  <span
    className={classNames(
      'shrink-0 rounded-full',
      status ? STATUS_STYLES[status].dot : 'border border-border-strong',
      className,
    )}
  />
);

/** Explains the colours of the loading/unloading icons in the grid. */
export const StatusLegend: React.FC = () => (
  <div className="flex items-center gap-3.5 text-2xs">
    <span className="text-fg-muted">Status laden/lossen</span>
    {STATUSES.map((status) => (
      <span key={status} className="flex items-center gap-1.5 text-fg-secondary">
        <StatusBadge status={status}>
          <MapPinIcon weight="fill" />
        </StatusBadge>
        {STATUS_STYLES[status].label}
      </span>
    ))}
  </div>
);
